// src/components/league-dashboard/RosterPlanModal.js
//
// Drop-shuffle plan modal. Per ROSTER_PLAN_SPEC.md § B.5.2.
//
// Render-only component — all state is owned by the useRosterPlan
// hook. The modal renders panels based on hook state and emits
// callbacks for user actions. No API calls inside the modal.
//
// State machine (per § B.5.2):
//   select_drop  → user picks a drop player from active roster
//   loading_plans → /plan-add API call in flight
//   choose_plan  → plans displayed; user picks one; OR infeasible message
//   confirming   → /execute-plan API call in flight
//   done         → modal closes (parent observes state)
//   error        → user-readable error + recovery options
//   cancelled    → user pressed Cancel (parent observes state)

import React from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  Loader2,
  X,
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

const HUMAN_INFEASIBLE = {
  infeasible_no_shuffle:
    'No way to fit this player by dropping that one. Pick a different player to drop.',
  infeasible_drop_not_active:
    'This player is not in an active slot. Drop-shuffle only works for active players.',
  infeasible_drop_not_owned: 'This player is not on your team.',
  infeasible_self_drop: 'Cannot drop the player you are trying to add.',
  infeasible_wrong_phase:
    'Drop-shuffle is available during the regular season only.',
};

function humanizeInfeasible(reason) {
  return HUMAN_INFEASIBLE[reason] || 'Unable to generate plans for this drop.';
}

function playerLabel(p) {
  if (!p) return '';
  const fn = p?.info?.first_name || '';
  const ln = p?.info?.last_name || '';
  return `${fn} ${ln}`.trim() || p?.info?.name || 'Unknown';
}

export default function RosterPlanModal({
  candidate,
  currentRoster,
  state,
  plans,
  selectedDropPlayer,
  selectedPlan,
  errorClass,
  errorMessage,
  infeasibleReason,
  onPickDrop,
  onChangeDrop,
  onSelectPlan,
  onConfirm,
  onCancel,
  onRetry,
  onDone,
  isVisible,
}) {
  // Surface lifecycle transitions to parent
  React.useEffect(() => {
    if (state === 'done' && onDone) onDone();
  }, [state, onDone]);

  // Hide for terminal/idle states
  if (
    !isVisible ||
    state === 'idle' ||
    state === 'cancelled' ||
    state === 'done'
  ) {
    return null;
  }

  const candidateName = playerLabel(candidate);
  const candidatePosition = candidate?.info?.position || '';
  const candidateBaseline =
    candidate?.league_context?.financial?.baseline_price;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 1000001 }}
    >
      <div
        className={`${dynastyTheme.components.card.base} w-full max-w-md max-h-[90vh] overflow-y-auto`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className={`${dynastyTheme.components.heading.h3} mb-0`}>
              <span className="inline-flex items-center gap-2">
                <ArrowLeftRight
                  className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`}
                />
                Make room for {candidateName}
                {candidatePosition && ` (${candidatePosition})`}
                {candidateBaseline != null &&
                  `, $${Math.round(candidateBaseline)}`}
              </span>
            </h2>
            <button
              onClick={onCancel}
              aria-label="Close"
              className={`p-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-600/50 ${dynastyTheme.classes.transition}`}
            >
              <X className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            </button>
          </div>

          {/* Body */}
          {state === 'select_drop' && (
            <SelectDropPanel
              candidate={candidate}
              currentRoster={currentRoster}
              onPickDrop={onPickDrop}
            />
          )}

          {state === 'loading_plans' && <LoadingPanel label="Finding plans..." />}

          {state === 'choose_plan' && plans.length > 0 && (
            <ChoosePlanPanel
              candidate={candidate}
              selectedDropPlayer={selectedDropPlayer}
              plans={plans}
              selectedPlan={selectedPlan}
              onChangeDrop={onChangeDrop}
              onSelectPlan={onSelectPlan}
            />
          )}

          {state === 'choose_plan' && plans.length === 0 && (
            <InfeasiblePanel
              selectedDropPlayer={selectedDropPlayer}
              infeasibleReason={infeasibleReason}
              candidateName={candidateName}
              onChangeDrop={onChangeDrop}
            />
          )}

          {state === 'confirming' && <LoadingPanel label="Executing plan..." />}

          {state === 'error' && (
            <ErrorPanel
              errorClass={errorClass}
              errorMessage={errorMessage}
              onRetry={onRetry}
              onChangeDrop={onChangeDrop}
            />
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onCancel}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Cancel
            </button>

            {state === 'choose_plan' && plans.length > 0 && (
              <button
                onClick={onConfirm}
                disabled={!selectedPlan}
                className={
                  selectedPlan
                    ? dynastyTheme.utils.getComponent('button', 'primary', 'md')
                    : 'px-5 py-2.5 text-base bg-neutral-700/50 text-neutral-500 cursor-not-allowed rounded-md'
                }
              >
                <span className="inline-flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Confirm
                </span>
              </button>
            )}

            {state === 'choose_plan' && plans.length === 0 && (
              <button
                onClick={onChangeDrop}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
              >
                Pick a different drop
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Subpanels
// ============================================================

function SelectDropPanel({ candidate, currentRoster, onPickDrop }) {
  const activeRoster = (currentRoster || []).filter(
    (p) => p?.league_context?.roster?.status === 'active'
  );

  // "Likely productive drops" first: players whose current slot is in
  // the candidate's effective_eligibility set. Per § B.5.2 sort order.
  const candidateEligibility = new Set(
    candidate?.league_context?.roster?.effective_eligibility || []
  );

  const sortedRoster = [...activeRoster].sort((a, b) => {
    const aSlot = a?.league_context?.roster?.position || '';
    const bSlot = b?.league_context?.roster?.position || '';
    const aType = aSlot.split('_')[0];
    const bType = bSlot.split('_')[0];

    const aProductive = candidateEligibility.has(aType);
    const bProductive = candidateEligibility.has(bType);
    if (aProductive && !bProductive) return -1;
    if (!aProductive && bProductive) return 1;

    const aPos = a?.info?.position || '';
    const bPos = b?.info?.position || '';
    if (aPos !== bPos) return aPos.localeCompare(bPos);

    return playerLabel(a).localeCompare(playerLabel(b));
  });

  if (sortedRoster.length === 0) {
    return (
      <div
        className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} text-sm ${dynastyTheme.classes.text.neutralLight}`}
      >
        No active-slot players to drop.
      </div>
    );
  }

  return (
    <div>
      <p
        className={`text-sm mb-3 ${dynastyTheme.classes.text.neutralLight}`}
      >
        Pick a player to drop:
      </p>
      <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
        {sortedRoster.map((player) => {
          const slot = player?.league_context?.roster?.position;
          const salary = player?.league_context?.financial?.salary;
          const productive = candidateEligibility.has((slot || '').split('_')[0]);
          return (
            <button
              key={player?.ids?.league_player || player?.ids?.mlb}
              onClick={() => onPickDrop(player?.ids?.mlb)}
              className={`
                w-full p-3 rounded-lg border-2 cursor-pointer transition-all duration-200
                flex items-center justify-between text-left
                ${dynastyTheme.classes.border.neutral}
                hover:border-yellow-400/50 hover:bg-yellow-400/5
              `}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}
                >
                  {playerLabel(player)}
                </span>
                {productive && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${dynastyTheme.classes.bg.success} ${dynastyTheme.classes.text.black} font-medium`}
                  >
                    Direct swap
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}
                >
                  {slot}
                </span>
                {salary != null && (
                  <span
                    className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}
                  >
                    ${Math.round(salary)}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChoosePlanPanel({
  candidate,
  selectedDropPlayer,
  plans,
  selectedPlan,
  onChangeDrop,
  onSelectPlan,
}) {
  const dropName = playerLabel(selectedDropPlayer);
  const dropSlot = selectedDropPlayer?.league_context?.roster?.position;
  const dropSalary = selectedDropPlayer?.league_context?.financial?.salary;
  const candidateName = playerLabel(candidate);

  return (
    <div>
      <div
        className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} mb-3 flex items-center justify-between`}
      >
        <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
          Drop: <strong>{dropName}</strong>
          {dropSlot && (
            <span className={`ml-2 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              {dropSlot}
            </span>
          )}
          {dropSalary != null && (
            <span className={`ml-2 text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              ${Math.round(dropSalary)}
            </span>
          )}
        </span>
        <button
          onClick={onChangeDrop}
          className={`text-xs underline ${dynastyTheme.classes.text.primary} hover:text-yellow-300`}
        >
          change
        </button>
      </div>
      <p className={`text-sm mb-3 ${dynastyTheme.classes.text.neutralLight}`}>
        Choose how to fit {candidateName}:
      </p>
      <div className="space-y-2">
        {plans.map((plan) => {
          const isSelected =
            selectedPlan?.plan_fingerprint === plan.plan_fingerprint;
          const shapeLabel =
            plan.shape === 'direct_swap' ? 'Direct swap' : 'Drop with shuffle';
          return (
            <label
              key={plan.plan_fingerprint}
              className={`
                p-3 rounded-lg border-2 cursor-pointer transition-all duration-200
                flex items-start gap-3
                ${
                  isSelected
                    ? `${dynastyTheme.classes.border.primaryBright} bg-yellow-400/10`
                    : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5`
                }
              `}
            >
              <input
                type="radio"
                name="plan"
                checked={isSelected}
                onChange={() => onSelectPlan(plan)}
                className="mt-1"
              />
              <div className="flex-1">
                <div
                  className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}
                >
                  {shapeLabel}
                </div>
                <div
                  className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-0.5`}
                >
                  {plan.description}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function InfeasiblePanel({
  selectedDropPlayer,
  infeasibleReason,
  candidateName,
  onChangeDrop,
}) {
  const reasonText = humanizeInfeasible(infeasibleReason);
  const dropName = playerLabel(selectedDropPlayer);
  return (
    <div>
      <div
        className={`p-3 rounded-lg ${dynastyTheme.classes.bg.darkLighter} mb-3 flex items-center justify-between`}
      >
        <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
          Drop: <strong>{dropName}</strong>
        </span>
        <button
          onClick={onChangeDrop}
          className={`text-xs underline ${dynastyTheme.classes.text.primary} hover:text-yellow-300`}
        >
          change
        </button>
      </div>
      <div
        className={`${dynastyTheme.components.badge.warning} p-3 rounded-lg flex items-start gap-2`}
      >
        <AlertTriangle
          className={`w-5 h-5 ${dynastyTheme.classes.text.warning} mt-0.5 flex-shrink-0`}
        />
        <div className={`text-sm ${dynastyTheme.classes.text.warning}`}>
          {reasonText}
        </div>
      </div>
    </div>
  );
}

function LoadingPanel({ label }) {
  return (
    <div className="flex items-center justify-center py-8">
      <Loader2
        className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary}`}
      />
      <span className={`ml-3 text-sm ${dynastyTheme.classes.text.neutralLight}`}>
        {label}
      </span>
    </div>
  );
}

function ErrorPanel({ errorClass, errorMessage, onRetry, onChangeDrop }) {
  const showRetry = errorClass === 'database_error';
  return (
    <div
      className={`${dynastyTheme.components.badge.error} p-3 rounded-lg flex items-start gap-2`}
    >
      <AlertTriangle
        className={`w-5 h-5 ${dynastyTheme.classes.text.error} mt-0.5 flex-shrink-0`}
      />
      <div className="flex-1">
        <div className={`font-medium text-sm ${dynastyTheme.classes.text.error}`}>
          Error
        </div>
        <div
          className={`text-xs ${dynastyTheme.classes.text.error} mt-1`}
        >
          {errorMessage || 'Something went wrong'}
        </div>
        <div className="mt-2 flex gap-3">
          {showRetry && (
            <button
              onClick={onRetry}
              className={`text-xs underline ${dynastyTheme.classes.text.primary} hover:text-yellow-300`}
            >
              Retry
            </button>
          )}
          <button
            onClick={onChangeDrop}
            className={`text-xs underline ${dynastyTheme.classes.text.primary} hover:text-yellow-300`}
          >
            Pick a different drop
          </button>
        </div>
      </div>
    </div>
  );
}
